import constate from 'constate';
import dayjs from 'dayjs';
import { ChangeEvent, FocusEvent, useCallback, useEffect, useRef, useState } from 'react';

import { DB_DATE_FORMAT } from 'core/constants';
import { NotificationPosition } from 'core/enums';
import { useFocusInputFunctionality } from 'core/hooks';
import { useFormContext } from 'core/states';
import { isSubmitButtonBlur } from 'core/utils';
import { IShipmentType } from 'corporate/types';
import { useGetPartPricingMultiplier } from 'parts/hooks';
import { PricingSourceType } from 'pricing/enums';
import { PricingHelper } from 'pricing/helpers';
import { useCostDecimals } from 'pricing/hooks';
import { buildTradeAgreementsRequest } from 'quotes/components/QuotePartSelectSpa/utils';
import {
  PriceBasedOn,
  PricingStrategy,
  QuotePartUnifiedStatus,
  VendorQuoteValidUntilPrefill,
} from 'quotes/enums';
import {
  EMPTY_VENDOR_QUOTE_COST_DATA,
  QuotePartHelper,
  QuotePartPricingHelper,
} from 'quotes/helpers';
import { useCalculatePrices, useQuotePricingWindowMessage } from 'quotes/hooks';
import { mapSelectedSpaPricesRequest } from 'quotes/mappers/tradeAgreement';
import {
  ICustomerQuotePart,
  IQuotePartCostData,
  IQuotePartPriceData,
  ISelectedSpaFormValue,
  ITradeAgreement,
} from 'quotes/types';
import {
  clearCostValidityDates,
  COST_VALID_SINCE_FIELD_NAME,
  COST_VALID_UNTIL_FIELD_NAME,
  getTypedCostValidityDefaults,
  getVendorQuoteCost,
  getVendorQuoteValidityDefaults,
  getVendorQuoteValidUntilPrefill,
  parseCostValidityDate,
  parseSpaSelectionAvailable,
} from 'quotes/utils';

interface ICalculatePricesParams {
  quoteId: number;
  number: string;
  manufacturerId: number;
  vendorNumber: string;
  accountsPayableId?: string;
  quantity: number;
  uom: string;
  lineReference?: number;
}

interface IPricingStateProps {
  isAlternateVersionEnabled: boolean;
  priceDecimals: number;
  standardCostFieldName?: string;
  marginFieldName?: string;
  initialCostData: IQuotePartCostData;
  initialPriceData?: IQuotePartPriceData;
  initialUnifiedPartStatus?: QuotePartUnifiedStatus | null;
  initialHasQuantityBreaks?: boolean | null;
  initialSpaSelectionAvailable?: boolean | null;
  partNumber?: string;
  manufacturerId?: number;
  quoteId?: number;
  quotePart?: ICustomerQuotePart | null;
  enabledPricingMultiplier?: boolean;
  getCalculatePricesParams?: () => ICalculatePricesParams;
  onAfterProcessPrices?: (result: { pricesUom: Record<string, unknown>; price: number }) => void;
  externalDisabled?: boolean;
  notificationPosition?: NotificationPosition;
}

const usePricingState = ({
  isAlternateVersionEnabled,
  priceDecimals: externalPriceDecimals,
  standardCostFieldName = 'standardCost',
  marginFieldName = 'margin',
  initialCostData,
  initialPriceData = {},
  initialUnifiedPartStatus = null,
  initialHasQuantityBreaks = null,
  initialSpaSelectionAvailable = null,
  partNumber,
  manufacturerId,
  quoteId,
  quotePart = null,
  enabledPricingMultiplier = false,
  getCalculatePricesParams,
  onAfterProcessPrices,
  externalDisabled = false,
  notificationPosition = NotificationPosition.FORM,
}: IPricingStateProps) => {
  const { setValue, getValues } = useFormContext();
  const costDecimals = useCostDecimals();
  const { setFocusElementName } = useFocusInputFunctionality();

  const { calculatePrices: calculatePricesAction } = useCalculatePrices(notificationPosition);
  // Set while the engine is paused for the quote: Select SPA and its removal wait for it.
  const pricingWindowMessage = useQuotePricingWindowMessage(quoteId);

  const { pricingMultiplier } = useGetPartPricingMultiplier(
    { part: { number: partNumber, manufacturerId } },
    { enabled: enabledPricingMultiplier && !isAlternateVersionEnabled }
  );

  const getCalculatePricesParamsRef = useRef(getCalculatePricesParams);

  useEffect(() => {
    getCalculatePricesParamsRef.current = getCalculatePricesParams;
  }, [getCalculatePricesParams]);

  const [costData, setCostDataState] = useState<IQuotePartCostData>(initialCostData);
  // Canonical read path for callbacks. The state value lags by one render after
  // setCostData, so any callback that reads costData.* mid-async-chain (e.g.
  // onBlurReplacementCost → onCostingCustomChange → calculatePrices) would see
  // the pre-update value. Read via costDataRef.current instead; setCostData
  // below updates the ref synchronously before scheduling the state update.
  // Treat costData state as the render-time snapshot only.
  const costDataRef = useRef<IQuotePartCostData>(initialCostData);
  // Set once the line's vendor or part changes: the dates given to its saved cost no longer hold.
  const isTypedCostValidityClearedRef = useRef(false);

  const setCostData = useCallback(
    (resolver: IQuotePartCostData | ((prev: IQuotePartCostData) => IQuotePartCostData)) => {
      const next = typeof resolver === 'function' ? resolver(costDataRef.current) : resolver;
      costDataRef.current = next;
      setCostDataState(next);
    },
    []
  );
  const [priceData, setPriceData] = useState<IQuotePartPriceData>(initialPriceData);
  const [priceDecimals, setPriceDecimals] = useState(externalPriceDecimals);
  const [unifiedPartStatus, setUnifiedPartStatus] = useState<QuotePartUnifiedStatus | null>(
    initialUnifiedPartStatus
  );
  const [isCalculating, setIsCalculating] = useState(false);
  const [purchaseCostExceedsStandard, setPurchaseCostExceedsStandard] = useState(false);
  // Holds the code: the Switch has hidden the pick from `selectedSpa` by the time the prompt opens.
  const [pendingReplaceSelectedSpa, setPendingReplaceSelectedSpa] = useState<{
    agreementCode?: string;
  } | null>(null);
  const [isExternalDisabled, setExternalDisabled] = useState(externalDisabled);
  const [previousPricingValues, setPreviousPricingValues] = useState<{
    purchaseCost: number | null;
    shipmentType: IShipmentType | null;
    corporateLocation: { externalLocationId?: string | number; id?: number } | null;
    pricingDate: string | null;
    pricingStrategy: PricingStrategy | null;
    prioritizeLegacyCost: boolean;
    quantity: number | null;
    uom: string | null;
    accountsPayableId: string | null;
  }>({
    purchaseCost: null,
    shipmentType: null,
    corporateLocation: null,
    pricingDate: null,
    pricingStrategy: null,
    prioritizeLegacyCost: false,
    quantity: null,
    uom: null,
    accountsPayableId: null,
  });

  const onAfterProcessPricesRef = useRef(onAfterProcessPrices);

  useEffect(() => {
    onAfterProcessPricesRef.current = onAfterProcessPrices;
  }, [onAfterProcessPrices]);

  const priceDataRef = useRef(priceData);
  const lastPricesRef = useRef<{
    discountedPrice: number | null;
    priceSourceType: PricingSourceType | null;
  }>({ discountedPrice: null, priceSourceType: null });

  // calculateStandardCostForReplacementCost is defined below calculatePrices; the ref lets
  // calculatePrices await the deferred-standard-cost re-derive without a forward reference (and
  // without adding the callback to its deps).
  const calculateStandardCostForReplacementCostRef = useRef<
    ((replacementCost: number) => Promise<void>) | null
  >(null);

  // Re-entrancy latch for the /quote/part/prices pipeline (see calculatePrices). A ref, not state,
  // so it is immune to the stale closures that defeat the value-based dedup below.
  const isCalculatingPricesRef = useRef(false);

  // Makes the next calculatePrices run skip the value-based dedup — never the re-entrancy latch.
  // A sourcing change (switching Overwrite off) is a mandatory refetch even when no pricing value
  // moved. A ref rather than an argument: prettier un-hugs a useCallback whose callback takes a
  // parameter, reformatting the whole 200-line body.
  const forceNextPricesRef = useRef(false);

  // A quantity blur on a picked line must re-price; calculatePrices' quantity shortcut skips it.
  const forceNextPrices = useCallback(() => {
    forceNextPricesRef.current = true;
  }, []);

  // Lets calculatePrices replay itself from its own `finally` (see the force replay there) without
  // a forward reference or a self-dependency.
  const calculatePricesRef = useRef<(() => Promise<void>) | null>(null);

  // Tri-state vendor quantity-breaks flag for the active part/UOM; null = unknown = fail open.
  // A ref so calculatePrices reads it synchronously; it drives no UI.
  const hasQuantityBreaksRef = useRef<boolean | null>(initialHasQuantityBreaks);

  const resetQuantityBreaks = useCallback(() => {
    hasQuantityBreaksRef.current = null;
  }, []);

  // Seeded from the row, then owned by every /prices answer.
  const [isSpaSelectionAvailable, setIsSpaSelectionAvailableState] = useState<boolean | null>(
    initialSpaSelectionAvailable
  );
  // The UOM re-ask reads the flag right after the /prices answer that set it, before a render.
  const isSpaSelectionAvailableRef = useRef<boolean | null>(initialSpaSelectionAvailable);

  const setIsSpaSelectionAvailable = useCallback((value: boolean | null) => {
    isSpaSelectionAvailableRef.current = value;
    setIsSpaSelectionAvailableState(value);
  }, []);

  const resetSpaSelectionAvailable = useCallback(() => {
    setIsSpaSelectionAvailable(null);
  }, [setIsSpaSelectionAvailable]);

  // Not read from the list cache: the next edit changes the request, and the cache has no answer.
  const [isSelectedSpaNotOffered, setIsSelectedSpaNotOfferedState] = useState(false);
  // selectSpa's rollback must restore the value set right before it, not the last render's.
  const isSelectedSpaNotOfferedRef = useRef(false);

  const setIsSelectedSpaNotOffered = useCallback((value: boolean) => {
    isSelectedSpaNotOfferedRef.current = value;
    setIsSelectedSpaNotOfferedState(value);
  }, []);

  // Built from the form, not the saved line; one builder so its consumers share the cached answer.
  const buildSelectSpaRequest = useCallback(
    () =>
      buildTradeAgreementsRequest({
        // The pick /prices is directed at, as in calculatePrices.
        hasSelectedSpa:
          isAlternateVersionEnabled &&
          !getValues('isCostingCustom') &&
          !!(getValues('selectedSpa') as ISelectedSpaFormValue | null)?.spaCostSource,
        isSpaSelectionAvailable: isSpaSelectionAvailableRef.current,
        quoteId,
        quotePart,
        values: getValues(),
      }),
    [getValues, isAlternateVersionEnabled, quoteId, quotePart]
  );

  // Synchronous mirror of isCalculating, for the blur-handler focus gates that must know
  // mid-dispatch whether the request they just kicked actually went out (the state value is stale
  // inside the same event). Unlike the latch above — a re-entry mutex for calculatePrices alone —
  // this tracks BOTH request functions (calculatePrices and the standalone replacement-cost
  // derive), which is why the two refs cannot be merged.
  const isCalculatingRef = useRef(false);

  // Keeps the blur of a Select SPA press from moving focus. A latch, not relatedTarget: Safari and
  // Firefox on macOS do not focus a clicked button.
  const selectSpaPressRef = useRef(false);

  const markSelectSpaPress = useCallback(() => {
    selectSpaPressRef.current = true;
    setTimeout(() => {
      selectSpaPressRef.current = false;
    });
  }, []);

  const isSelectSpaPressed = useCallback(() => selectSpaPressRef.current, []);

  useEffect(() => {
    priceDataRef.current = priceData;
  }, [priceData]);

  useEffect(() => {
    setValue('isPricingCustom', priceData.priceSourceType === PricingSourceType.CUSTOM);
  }, [priceData.priceSourceType, setValue]);

  // Pricing answers for a picked line leave the default agreement out; only the cost data keeps it.
  useEffect(() => {
    if (isAlternateVersionEnabled) {
      setValue(
        'defaultSpaAgreement',
        QuotePartPricingHelper.mapCostDataToDefaultSpaAgreement(costData)
      );
    }
  }, [costData, isAlternateVersionEnabled, setValue]);

  // The schema and the payload read the live cost's vendor quote from the form.
  useEffect(() => {
    if (isAlternateVersionEnabled) {
      setValue('vendorQuoteCost', getVendorQuoteCost(costData));
    }
  }, [costData, isAlternateVersionEnabled, setValue]);

  useEffect(() => {
    setCostData(initialCostData);
    setPriceData(initialPriceData);
    isTypedCostValidityClearedRef.current = false;

    setPurchaseCostExceedsStandard(
      QuotePartPricingHelper.doesPurchaseCostExceedStandard(
        getValues('purchaseCost'),
        getValues(standardCostFieldName),
        initialCostData?.costSourceType
      )
    );
    // getValues should not be included as a dependency - we only need the initial form values
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCostData, initialPriceData]);

  useEffect(() => {
    setUnifiedPartStatus(initialUnifiedPartStatus);
  }, [initialUnifiedPartStatus]);

  useEffect(() => {
    hasQuantityBreaksRef.current = initialHasQuantityBreaks;
  }, [initialHasQuantityBreaks]);

  // Keyed on the cost-data seed too: the provider stays mounted across lines.
  useEffect(() => {
    setIsSpaSelectionAvailable(initialSpaSelectionAvailable);
    setIsSelectedSpaNotOffered(false);
  }, [
    initialCostData,
    initialSpaSelectionAvailable,
    setIsSelectedSpaNotOffered,
    setIsSpaSelectionAvailable,
  ]);

  useEffect(() => {
    setPriceDecimals(externalPriceDecimals);
  }, [externalPriceDecimals]);

  const disablePricingFields = isExternalDisabled || isCalculating;

  const initializePreviousPricingValues = useCallback(() => {
    const currentPricingDate = getValues('pricingDate');

    setPreviousPricingValues({
      purchaseCost: Number(getValues('purchaseCost')),
      shipmentType: getValues('shipmentType'),
      corporateLocation: getValues('corporateLocation'),
      pricingDate: currentPricingDate ? dayjs(currentPricingDate).format(DB_DATE_FORMAT) : null,
      pricingStrategy: (getValues('pricingStrategy') as PricingStrategy) ?? null,
      prioritizeLegacyCost: !!getValues('prioritizeLegacyCost'),
      quantity: getValues('quantity') ?? null,
      uom: getValues('uom') ?? null,
      accountsPayableId: getValues('accountsPayableId') ?? null,
    });
  }, [getValues]);

  const changePriceSource = useCallback((priceSourceType: PricingSourceType | null) => {
    setPriceData((prev) =>
      QuotePartPricingHelper.sanitizePriceDataForSourceType(prev, priceSourceType)
    );
  }, []);

  const processCostResponse = useCallback(
    (pricesUom: Record<string, unknown>, isInitialLoad = false, uom?: string) => {
      const previousCostData = costDataRef.current;
      const responseCostData = QuotePartPricingHelper.mapPricesUomToCostData(
        pricesUom,
        isAlternateVersionEnabled,
        costDataRef.current,
        uom ?? getCalculatePricesParamsRef.current?.()?.uom
      );

      // /quote/part/prices tags an echoed pick cost CUSTOM, so a picked line keeps its attribution.
      const {
        costData: resolvedCostData,
        purchaseCost: pickPurchaseCost,
        shouldAdoptCostSourceType,
      } = QuotePartPricingHelper.resolveSelectedSpaCostResponse(
        costDataRef.current,
        QuotePartPricingHelper.sanitizeCostDataForSourceType(
          responseCostData,
          responseCostData.costSourceType ?? null
        )
      );

      setCostData(resolvedCostData);

      const hasSelectedSpaPick = !shouldAdoptCostSourceType;

      // BE may return stringy zeros (e.g. "0.00000") for missing cost fields.
      // Coerce here so every downstream reader sees `number | null` and stringy
      // zeros never leak into the form state.
      const cost = Number(pricesUom?.cost) || null;
      const purchaseCost = hasSelectedSpaPick
        ? pickPurchaseCost
        : Number(pricesUom?.purchaseCost) || null;
      const externalReplacementStandardCost =
        Number(pricesUom?.externalReplacementStandardCost) || null;

      setValue(standardCostFieldName, cost);
      setValue('purchaseCost', purchaseCost);

      const costSourceType = hasSelectedSpaPick
        ? PricingSourceType.SELECTED_SPA
        : pricesUom?.costSourceType || pricesUom?.costSource;

      setPurchaseCostExceedsStandard(
        QuotePartPricingHelper.doesPurchaseCostExceedStandard(
          purchaseCost,
          cost,
          costSourceType as string
        )
      );

      const isCustomCostSource = costSourceType === PricingSourceType.CUSTOM;

      // A held pick owns the toggle: the response's CUSTOM tag would switch Overwrite on.
      if (!hasSelectedSpaPick) {
        if (isInitialLoad) {
          // Initial part load: a part with no cost defaults to Overwrite so the cost is editable.
          setValue('isCostingCustom', isCustomCostSource || !purchaseCost);
        } else if (purchaseCost || isCustomCostSource) {
          // Recalc with a real cost reflects the source; with no cost leave the toggle as the user
          // set it, so an explicit "off" survives shipment/location/quantity/date changes.
          setValue('isCostingCustom', isCustomCostSource);
        }
      }

      if (!getValues('isReplacementCostCustom') && !getValues('prioritizeLegacyCost')) {
        const isReplacementMode = getValues('priceBasedOn') === PriceBasedOn.REPLACEMENT;
        // Capture the pre-overwrite values so the deferred-lifecycle decision can compare the
        // response's replacement cost against the one the current standard cost was derived from.
        const priorReplacementStandardCost = Number(getValues('replacementStandardCost')) || null;
        const priorReplacementCost = Number(getValues('replacementCost')) || null;
        const responseReplacementCost = Number(pricesUom?.externalReplacementCost) || null;

        // Read the coerced value, not the raw field: a stringy "0.00000" is truthy and would both
        // clear the Overwrite toggle and render $0.00000 in the input.
        setValue('isReplacementCostCustom', !responseReplacementCost);
        setValue('replacementCost', responseReplacementCost);
        setValue('replacementCostNumber', pricesUom?.externalRepCostRegistrationNum ?? null);

        if (isReplacementMode) {
          const { nextReplacementStandardCost } =
            QuotePartPricingHelper.resolveReplacementStandardCostUpdate({
              priorReplacementStandardCost,
              priorReplacementCost,
              responseReplacementCost,
              responseReplacementStandardCost: externalReplacementStandardCost,
              costDecimals,
            });

          // A changed external replacement cost clears the stale standard cost; its fresh value
          // rides the same response via the fetch_replacement_standard_cost flag (trust branch
          // above), and the manual basis re-derives at the end of calculatePrices.
          setValue('replacementStandardCost', nextReplacementStandardCost);
        } else {
          // Deferred lifecycle invariant: quoted mode never displays a replacement standard cost.
          // The BE still returns external_replacement_standard_cost for a line whose PERSISTED
          // price_based_on is replacement (the drawer's unsaved switch to quoted isn't visible to
          // the generic recalc), so trusting the response here would repopulate the quoted card.
          // Force null — the value re-derives when the user selects the replacement radio.
          setValue('replacementStandardCost', null);
        }
      }

      // Sync the vendor quote number from the response only while the Overwrite toggle — as just
      // resolved from this same response above — is OFF, i.e. only while the input is disabled.
      // With the toggle on the input is editable (possibly mid-typing during the calc) and must
      // not be clobbered; when this response revoked the override, the source's number replaces
      // any orphaned custom value before the input greys out. A held pick is excluded: the BE
      // stores no vendor quote number for a selected line.
      if (!hasSelectedSpaPick && !getValues('isCostingCustom')) {
        setValue('costSource.vendorQuoteNumber', QuotePartHelper.getVendorQuoteNumber(pricesUom));

        // As the BE seeds the line on save, so the form posts what it shows.
        switch (
          getVendorQuoteValidUntilPrefill({
            initialCostData,
            nextCostData: resolvedCostData,
            previousCostData,
          })
        ) {
          case VendorQuoteValidUntilPrefill.SAVED:
            setValue(
              COST_VALID_UNTIL_FIELD_NAME,
              getVendorQuoteValidityDefaults(
                isTypedCostValidityClearedRef.current || !quotePart?.id ? null : quotePart
              ).validUntil
            );
            break;
          case VendorQuoteValidUntilPrefill.SHARED:
            setValue(
              COST_VALID_UNTIL_FIELD_NAME,
              parseCostValidityDate(resolvedCostData.costSourceSharedValidUntil)
            );
            break;
          default:
            break;
        }
      }
    },
    [
      costDecimals,
      getValues,
      initialCostData,
      isAlternateVersionEnabled,
      quotePart,
      setCostData,
      setValue,
      standardCostFieldName,
    ]
  );

  const processPriceResponse = useCallback(
    (pricesUom: Record<string, unknown>): number => {
      setPriceData((prev) =>
        prev.priceSourceType !== PricingSourceType.CUSTOM
          ? QuotePartPricingHelper.mapPricesUomToPriceData(pricesUom, isAlternateVersionEnabled)
          : prev
      );

      let priceValue = Number(getValues('price'));
      const quotedStdCost = pricesUom?.cost as number;
      const discountedPrice = Number(pricesUom?.discountedPrice);
      const isReplacementMode = getValues('priceBasedOn') === PriceBasedOn.REPLACEMENT;
      const isSourceCustom = priceDataRef.current.priceSourceType === PricingSourceType.CUSTOM;

      lastPricesRef.current = {
        discountedPrice: discountedPrice || null,
        priceSourceType: (pricesUom?.priceSourceType as PricingSourceType) || null,
      };

      if (isSourceCustom) {
        // Custom source → preserve margin, recalculate price from updated cost
        const existingMargin = parseFloat(getValues('pricingMargin'));
        const activeCost = isReplacementMode
          ? Number(getValues('replacementStandardCost')) || quotedStdCost
          : quotedStdCost;

        if (!isNaN(existingMargin) && activeCost) {
          const newPrice = PricingHelper.computeAndFormatPrice(activeCost, existingMargin / 100, {
            decimals: priceDecimals,
          });

          setValue('price', newPrice);
          priceValue = Number(newPrice);
        }
      } else if (!isReplacementMode) {
        // Non-custom quoted → use API price, update source and pricing margin
        setValue('price', discountedPrice || null);
        priceValue = discountedPrice || 0;

        changePriceSource(lastPricesRef.current.priceSourceType);

        if (priceValue) {
          setValue('pricingMargin', PricingHelper.computeGpmPercentage(quotedStdCost, priceValue));
        }
      }
      // Non-custom replacement → keep price as-is

      // True margin: always from quoted cost
      setValue(marginFieldName, PricingHelper.computeGpmPercentage(quotedStdCost, priceValue));

      return priceValue;
    },
    [
      changePriceSource,
      getValues,
      isAlternateVersionEnabled,
      marginFieldName,
      priceDecimals,
      setValue,
    ]
  );

  const processPricingResponse = useCallback(
    (pricesUom: Record<string, unknown>, isInitialLoad = false, uom?: string) => {
      if (!pricesUom) {
        return;
      }

      // Refresh from this response's active-UOM entry. Absent key ⇒ unknown (null), never false.
      hasQuantityBreaksRef.current =
        typeof pricesUom.hasQuantityBreaks === 'boolean' ? pricesUom.hasQuantityBreaks : null;
      setIsSpaSelectionAvailable(
        parseSpaSelectionAvailable(pricesUom.externalSpaSelectionAvailable)
      );

      processCostResponse(pricesUom, isInitialLoad, uom);
      const priceValue = processPriceResponse(pricesUom);

      onAfterProcessPricesRef.current?.({ pricesUom, price: priceValue });
    },
    [processCostResponse, processPriceResponse, setIsSpaSelectionAvailable]
  );

  const calculatePrices = useCallback(async () => {
    // Re-entrancy guard: only one prices pipeline runs at a time. Setting isCalculating disables
    // the focused trigger field, and disabling a focused input fires a native blur whose on-blur
    // handler calls calculatePrices again — a re-entrant echo the value-based
    // `hasPricingFieldsChanged` dedup below CANNOT stop (previousPricingValues isn't updated until
    // the in-flight response lands, so the stale closure always reads "changed"), which otherwise
    // storms /quote/part/prices without end. Legitimate new calls aren't dropped: every trigger
    // field is disabled while a calc is in flight (the vendor quote / agreement number inputs stay
    // enabled mid-calc, but they never trigger one), so a fresh user-initiated recalc can only
    // begin after `finally` clears the latch.
    if (isCalculatingPricesRef.current) {
      return;
    }

    if (!getCalculatePricesParamsRef.current) {
      return;
    }

    const partParams = getCalculatePricesParamsRef.current();
    const currentPurchaseCost = Number(getValues('purchaseCost'));
    const currentShipmentType = getValues('shipmentType');
    const currentCorporateLocation = getValues('corporateLocation');
    const currentPricingDate = getValues('pricingDate');
    const currentPricingDateStr = currentPricingDate
      ? dayjs(currentPricingDate).format(DB_DATE_FORMAT)
      : null;
    const currentPricingStrategy = isAlternateVersionEnabled
      ? (getValues('pricingStrategy') as PricingStrategy) ?? null
      : null;
    const currentPrioritizeLegacyCost = !!getValues('prioritizeLegacyCost');
    const currentQuantity = partParams.quantity ?? null;
    const currentUom = partParams.uom ?? null;
    const currentAccountsPayableId = partParams.accountsPayableId ?? null;

    const purchaseCostChanged = currentPurchaseCost !== previousPricingValues.purchaseCost;
    const shipmentTypeChanged =
      currentShipmentType?.alternateCode !== previousPricingValues.shipmentType?.alternateCode;
    const corporateLocationChanged =
      currentCorporateLocation?.externalLocationId !==
      previousPricingValues.corporateLocation?.externalLocationId;
    const pricingDateChanged = currentPricingDateStr !== previousPricingValues.pricingDate;
    const pricingStrategyChanged = currentPricingStrategy !== previousPricingValues.pricingStrategy;
    const prioritizeLegacyCostChanged =
      currentPrioritizeLegacyCost !== previousPricingValues.prioritizeLegacyCost;
    const quantityChanged = currentQuantity !== previousPricingValues.quantity;
    const uomChanged = currentUom !== previousPricingValues.uom;
    const accountsPayableIdChanged =
      currentAccountsPayableId !== previousPricingValues.accountsPayableId;

    // On an alternate-version quote whose active part has no vendor quantity breaks (strict false),
    // a quantity-only edit can't move cost/price, so it must NOT trigger the /prices call. Every
    // other changed dimension still fires. Unknown (null/undefined) fails open, counting qty.
    const suppressQuantityTrigger =
      isAlternateVersionEnabled && hasQuantityBreaksRef.current === false;
    const effectiveQuantityChanged = quantityChanged && !suppressQuantityTrigger;

    const hasPricingFieldsChanged =
      purchaseCostChanged ||
      shipmentTypeChanged ||
      corporateLocationChanged ||
      pricingDateChanged ||
      pricingStrategyChanged ||
      prioritizeLegacyCostChanged ||
      effectiveQuantityChanged ||
      uomChanged ||
      accountsPayableIdChanged;

    // Consumed here rather than on entry so a call dropped by the re-entrancy latch (or the params
    // guard) leaves the flag set for the run that actually reaches this point. Nothing stops a
    // force being raised WHILE a run holds the latch — the Switch's disabled prop makes that
    // near-unreachable, not impossible — so the `finally` below replays a flag left standing.
    const forced = forceNextPricesRef.current;
    forceNextPricesRef.current = false;

    if (!forced && !hasPricingFieldsChanged) {
      // Reachable with quantityChanged only in the suppressed qty-only case (any other changed
      // dimension makes hasPricingFieldsChanged true). Record the new quantity so the NEXT genuine
      // trigger sends the current qty and isn't dedup-skipped. Returns before the latch line, so
      // the re-entrancy latch / isCalculating stay untouched.
      if (quantityChanged) {
        setPreviousPricingValues((prev) => ({ ...prev, quantity: currentQuantity }));
      }

      return;
    }

    isCalculatingPricesRef.current = true;
    isCalculatingRef.current = true;
    setIsCalculating(true);

    const currentCostData = costDataRef.current;

    const isCostingCustom = !PricingHelper.areEqual(
      currentCostData.defaultPurchaseCost,
      getValues('purchaseCost'),
      priceDecimals
    );

    const shouldSendPurchaseCost = isAlternateVersionEnabled
      ? currentCostData.costSourceType !== PricingSourceType.EXTERNAL
      : isCostingCustom;

    // Opt in to the BE deriving external_replacement_standard_cost for THIS request's inputs (ship
    // type / location / qty / date), so a replacement-mode recalc gets the fresh standard cost in
    // the same response instead of a separate derive call. Only for the EXTERNAL basis: with
    // Overwrite on the manual basis wins and is derived client-side, and prioritizeLegacyCost lines
    // don't auto-manage the replacement cost. The flag is passed only when truthy; the service
    // never puts the key on the wire when off (BE (bool)-casts the query string).
    const shouldFetchReplacementStandardCost =
      getValues('priceBasedOn') === PriceBasedOn.REPLACEMENT &&
      !getValues('isReplacementCostCustom') &&
      !currentPrioritizeLegacyCost;

    // Names the held pick, as the save does, so the BE directs the pricing call at it.
    const heldPick =
      isAlternateVersionEnabled && !getValues('isCostingCustom')
        ? (getValues('selectedSpa') as ISelectedSpaFormValue | null)
        : null;

    try {
      const { record, success } = await calculatePricesAction({
        ...partParams,
        shipType: currentShipmentType?.alternateCode,
        shippingLocationId: currentCorporateLocation?.externalLocationId,
        purchaseCost: shouldSendPurchaseCost ? getValues('purchaseCost') : null,
        pricingDate: currentPricingDateStr,
        pricingStrategy: currentPricingStrategy,
        prioritizeLegacyCost: currentPrioritizeLegacyCost ? 1 : 0,
        ...(shouldFetchReplacementStandardCost ? { fetchReplacementStandardCost: 1 } : {}),
        ...mapSelectedSpaPricesRequest(heldPick),
      });

      if (success && record) {
        const prices = record as Record<string, Record<string, unknown>>;
        const pricesUom = prices[partParams.uom];

        if (pricesUom) {
          processPricingResponse(pricesUom, false, partParams.uom);

          // Snapshot the post-API purchaseCost so the next dedup compare matches
          // the form value (BE may normalize or clear it).
          setPreviousPricingValues({
            purchaseCost:
              pricesUom.purchaseCost != null ? Number(pricesUom.purchaseCost) : currentPurchaseCost,
            shipmentType: currentShipmentType,
            corporateLocation: currentCorporateLocation,
            pricingDate: currentPricingDateStr,
            pricingStrategy: currentPricingStrategy,
            prioritizeLegacyCost: currentPrioritizeLegacyCost,
            quantity: currentQuantity,
            uom: currentUom,
            accountsPayableId: currentAccountsPayableId,
          });

          // Deferred replacement standard cost, MANUAL basis only. The EXTERNAL basis now arrives
          // in this same response via the fetchReplacementStandardCost flag (processCostResponse's
          // "response value present → trust" branch sets it and processPriceResponse reprices from
          // the kept margin), so it needs no client-side re-derive. The BE cannot derive the manual
          // override, so when Overwrite is on we still derive it here from the manually entered
          // replacement cost — its standard cost also depends on ship type / location / date /
          // strategy / quantity / uom, all just refreshed. Awaited so the spinner stays up and
          // callers like onPriceBasedOnChange see the settled value; the input memo inside
          // calculateStandardCostForReplacementCost no-ops it when nothing derive-relevant changed.
          if (
            getValues('isReplacementCostCustom') &&
            getValues('priceBasedOn') === PriceBasedOn.REPLACEMENT &&
            !getValues('prioritizeLegacyCost')
          ) {
            const replacementBasis = Number(getValues('replacementCost')) || null;

            if (replacementBasis) {
              await calculateStandardCostForReplacementCostRef.current?.(replacementBasis);
            }
          } else if (
            shouldFetchReplacementStandardCost &&
            Number(getValues('replacementStandardCost'))
          ) {
            // The flag path derived external_replacement_standard_cost BE-side and
            // processCostResponse already trusted it. Record the SAME derive inputs in the memo
            // (identical tuple to the one calculateStandardCostForReplacementCost writes on
            // success, reusing this call's own locals) so a following explicit derive — e.g.
            // VendorFormField's post-vendor call — dedups instead of re-fetching the BE's value.
            lastReplacementCostCalcRef.current = {
              replacementCost: Number(getValues('replacementCost')) || null,
              shipType: currentShipmentType?.alternateCode ?? null,
              shippingLocationId: currentCorporateLocation?.externalLocationId ?? null,
              pricingDate: currentPricingDateStr,
              pricingStrategy: currentPricingStrategy,
              quantity: currentQuantity,
              uom: currentUom,
              accountsPayableId: currentAccountsPayableId,
            };
          }
        } else {
          // Successful ask with no entry for the asked UOM — authoritative
          // "vendor can't price this part in this UOM". Snapshot the ask so
          // switching away and back re-fires (without this, prev.uom keeps the
          // old UOM and the return trip dedups to a no-op), and drop the
          // previous UOM's quantity-breaks flag: unknown ⇒ fail open.
          hasQuantityBreaksRef.current = null;
          setIsSpaSelectionAvailable(null);

          setPreviousPricingValues({
            purchaseCost: currentPurchaseCost,
            shipmentType: currentShipmentType,
            corporateLocation: currentCorporateLocation,
            pricingDate: currentPricingDateStr,
            pricingStrategy: currentPricingStrategy,
            prioritizeLegacyCost: currentPrioritizeLegacyCost,
            quantity: currentQuantity,
            uom: currentUom,
            accountsPayableId: currentAccountsPayableId,
          });
        }
      }
    } finally {
      isCalculatingRef.current = false;
      setIsCalculating(false);
      isCalculatingPricesRef.current = false;

      // A force raised while this run held the latch was never consumed (the re-entrant call
      // returned at the latch, before the consume point). Replay it now that the latch is clear so
      // the sourcing change still gets its mandatory refetch. Not awaited: this runs inside
      // `finally`, so awaiting would hold the caller open for a request it never asked for.
      if (forceNextPricesRef.current) {
        void calculatePricesRef.current?.();
      }
    }
  }, [
    calculatePricesAction,
    getValues,
    isAlternateVersionEnabled,
    priceDecimals,
    previousPricingValues,
    processPricingResponse,
    setIsSpaSelectionAvailable,
  ]);

  useEffect(() => {
    calculatePricesRef.current = calculatePrices;
  }, [calculatePrices]);

  const lastReplacementCostCalcRef = useRef<{
    replacementCost: number | null;
    shipType: string | null;
    shippingLocationId: string | number | null;
    pricingDate: string | null;
    pricingStrategy: PricingStrategy | null;
    quantity: number | null;
    uom: string | null;
    accountsPayableId: string | null;
  }>({
    replacementCost: null,
    shipType: null,
    shippingLocationId: null,
    pricingDate: null,
    pricingStrategy: null,
    quantity: null,
    uom: null,
    accountsPayableId: null,
  });

  const calculateStandardCostForReplacementCost = useCallback(
    async (replacementCost: number) => {
      // Deferred lifecycle invariant, defense in depth: the replacement standard cost only exists
      // when pricing is actually based on replacement. Never derive (nor write it) in quoted mode,
      // so no caller — present or future — can repopulate the deferred card while quoted.
      if (getValues('priceBasedOn') !== PriceBasedOn.REPLACEMENT) {
        return;
      }

      if (!getCalculatePricesParamsRef.current || replacementCost == null || replacementCost <= 0) {
        return;
      }

      const partParams = getCalculatePricesParamsRef.current();
      const currentShipmentType = getValues('shipmentType');
      const currentCorporateLocation = getValues('corporateLocation');
      const currentPricingDate = getValues('pricingDate');
      const currentPricingDateStr = currentPricingDate
        ? dayjs(currentPricingDate).format(DB_DATE_FORMAT)
        : null;
      const currentPricingStrategy = isAlternateVersionEnabled
        ? (getValues('pricingStrategy') as PricingStrategy) ?? null
        : null;
      const shipType = currentShipmentType?.alternateCode ?? null;
      const shippingLocationId = currentCorporateLocation?.externalLocationId ?? null;
      const currentQuantity = partParams.quantity ?? null;
      const currentUom = partParams.uom ?? null;
      const currentAccountsPayableId = partParams.accountsPayableId ?? null;

      const lastCall = lastReplacementCostCalcRef.current;
      const inputsUnchanged =
        lastCall.replacementCost === replacementCost &&
        lastCall.shipType === shipType &&
        lastCall.shippingLocationId === shippingLocationId &&
        lastCall.pricingDate === currentPricingDateStr &&
        lastCall.pricingStrategy === currentPricingStrategy &&
        lastCall.quantity === currentQuantity &&
        lastCall.uom === currentUom &&
        lastCall.accountsPayableId === currentAccountsPayableId;

      if (inputsUnchanged) {
        return;
      }

      isCalculatingRef.current = true;
      setIsCalculating(true);

      try {
        // A T-Item answer's SPA flag is always false: read `cost`, never processPricingResponse.
        const { record, success } = await calculatePricesAction({
          ...partParams,
          shipType: currentShipmentType?.alternateCode,
          shippingLocationId: currentCorporateLocation?.externalLocationId,
          purchaseCost: replacementCost,
          pricingDate: currentPricingDateStr,
          pricingStrategy: currentPricingStrategy,
          ignoreExternalBestCost: 1,
        });

        if (success && record) {
          const prices = record as Record<string, Record<string, unknown>>;
          const pricesUom = prices[partParams.uom];
          const replacementStandardCost = pricesUom?.cost as number;

          // Memoize on success only — failed responses stay retryable on re-blur.
          if (replacementStandardCost != null && replacementStandardCost > 0) {
            lastReplacementCostCalcRef.current = {
              replacementCost,
              shipType,
              shippingLocationId,
              pricingDate: currentPricingDateStr,
              pricingStrategy: currentPricingStrategy,
              quantity: currentQuantity,
              accountsPayableId: currentAccountsPayableId,
              uom: currentUom,
            };

            setValue('replacementStandardCost', replacementStandardCost);

            const isReplacementMode = getValues('priceBasedOn') === PriceBasedOn.REPLACEMENT;

            if (isReplacementMode) {
              // Replacement mode: keep pricing margin, adjust price and true margin
              const pricingMarginVal = getValues('pricingMargin');
              const existingPricingMargin = parseFloat(pricingMarginVal);

              if (pricingMarginVal != null && !isNaN(existingPricingMargin)) {
                const newPrice = PricingHelper.computeAndFormatPrice(
                  replacementStandardCost,
                  existingPricingMargin / 100,
                  { decimals: priceDecimals }
                );

                setValue('price', newPrice);
                setValue(
                  marginFieldName,
                  PricingHelper.computeGpmPercentage(
                    Number(getValues(standardCostFieldName)),
                    newPrice
                  )
                );
              } else {
                const priceValue = Number(getValues('price'));

                if (priceValue) {
                  setValue(
                    'pricingMargin',
                    PricingHelper.computeGpmPercentage(replacementStandardCost, priceValue)
                  );
                }
              }
            }
            // Not replacement mode: no margin change, no price change
          }
        }
      } finally {
        isCalculatingRef.current = false;
        setIsCalculating(false);
      }
    },
    [
      calculatePricesAction,
      getValues,
      isAlternateVersionEnabled,
      marginFieldName,
      priceDecimals,
      setValue,
      standardCostFieldName,
    ]
  );

  useEffect(() => {
    calculateStandardCostForReplacementCostRef.current = calculateStandardCostForReplacementCost;
  }, [calculateStandardCostForReplacementCost]);

  const resetReplacementStandardCostDedup = useCallback(() => {
    // Force the next calculateStandardCostForReplacementCost to re-fetch even when every derive
    // input is unchanged — e.g. toggling REPLACEMENT → QUOTED → REPLACEMENT with no field edits,
    // where the cleared standard cost must be re-derived rather than left empty. The BE pricing
    // cache dedups identical requests, so the round-trip is cheap.
    lastReplacementCostCalcRef.current = {
      replacementCost: null,
      shipType: null,
      shippingLocationId: null,
      pricingDate: null,
      pricingStrategy: null,
      quantity: null,
      uom: null,
      accountsPayableId: null,
    };
  }, []);

  // Hands focus to a companion user-entered field (Vendor Quote / Agreement Number) so its value
  // can be typed while the pricing request just kicked off by a cost blur is in flight. Fires only
  // for a genuine user blur (`event` filters programmatic callers) whose focus did not move to an
  // element the user can deliberately target — anything tab-reachable (tabIndex >= 0: inputs,
  // buttons, the next field on tab-out) wins. relatedTarget null alone cannot express "went
  // nowhere" here: the drawer's focus-trap container (tabIndex -1) catches clicks on empty space,
  // and that catch must still hand off. `requestStarted` (see kickPricingRequest) keeps dedup- and
  // latch-dropped blurs from stealing focus.
  const focusFieldWhileCalculating = useCallback(
    (
      event: FocusEvent<HTMLInputElement> | undefined,
      requestStarted: boolean,
      fieldName: string
    ): void => {
      const blurTarget = (event?.relatedTarget as HTMLElement | null) ?? null;
      const movedIntoInteractiveElement = null !== blurTarget && blurTarget.tabIndex >= 0;

      if (
        !event ||
        movedIntoInteractiveElement ||
        selectSpaPressRef.current ||
        !requestStarted ||
        isExternalDisabled
      ) {
        return;
      }

      setFocusElementName(fieldName);
    },
    [isExternalDisabled, setFocusElementName]
  );

  // Single home for the started-detection idiom: the request functions set isCalculatingRef
  // synchronously before their first await, so comparing the ref around the kick reveals whether
  // THIS kick actually launched a request — an async callee cannot return that synchronously
  // itself, and reading the ref alone cannot tell a fresh launch from one already in flight.
  const kickPricingRequest = useCallback(
    (kick: () => Promise<void>): { requestStarted: boolean; promise: Promise<void> } => {
      const wasCalculating = isCalculatingRef.current;
      const promise = kick();

      return { requestStarted: !wasCalculating && isCalculatingRef.current, promise };
    },
    []
  );

  const onChangePurchaseCost = useCallback(
    ({ source, target: { value: purchaseCost } }) => {
      if (!purchaseCost) {
        setPurchaseCostExceedsStandard(false);

        return;
      }

      if (source !== 'event') {
        return;
      }

      if (costDataRef.current.costSourceType !== PricingSourceType.CUSTOM) {
        setValue('costSource.vendorQuoteNumber', null);
        setCostData((prev) =>
          QuotePartPricingHelper.sanitizeCostDataForSourceType(
            prev,
            purchaseCost ? PricingSourceType.CUSTOM : null
          )
        );
      }

      if (isAlternateVersionEnabled || !pricingMultiplier) {
        setPurchaseCostExceedsStandard(false);

        return;
      }

      const price = getValues('price');
      const standardCost = PricingHelper.computePrice(
        purchaseCost,
        pricingMultiplier?.standardCost || 0
      );

      setValue('standardCost', standardCost);
      setValue('margin', PricingHelper.computeGpmPercentage(standardCost, price));

      setPurchaseCostExceedsStandard(
        QuotePartPricingHelper.doesPurchaseCostExceedStandard(
          purchaseCost,
          standardCost,
          PricingSourceType.CUSTOM
        )
      );
    },
    [getValues, isAlternateVersionEnabled, pricingMultiplier, setCostData, setValue]
  );

  const onBlurPurchaseCost = useCallback(
    async (event?: FocusEvent<HTMLInputElement>) => {
      if (!isAlternateVersionEnabled) {
        return;
      }

      // Blur caused by activating Save: the save path recomputes authoritatively, so skip
      // the wasted /quote/part/prices preview. Normal blur-while-editing still fires it.
      if (isSubmitButtonBlur(event)) {
        return;
      }

      const { requestStarted, promise: pricesPromise } = kickPricingRequest(calculatePrices);

      focusFieldWhileCalculating(
        event,
        requestStarted && !!getValues('isCostingCustom'),
        'costSource.vendorQuoteNumber'
      );

      await pricesPromise;
    },
    [
      calculatePrices,
      focusFieldWhileCalculating,
      getValues,
      isAlternateVersionEnabled,
      kickPricingRequest,
    ]
  );

  const restoreAutomaticCost = useCallback(
    async (isRevokingPick = false) => {
      const currentCostData = costDataRef.current;

      setPurchaseCostExceedsStandard(false);
      // The raw cost, not the rounded echo: the BE restores External only on a 4-decimal match.
      const externalPurchaseCost = isAlternateVersionEnabled
        ? currentCostData.externalPurchaseCost ?? currentCostData.defaultPurchaseCost
        : currentCostData.defaultPurchaseCost;
      // The cached cost answered the pick, and posting it would read as manually entered.
      const restoredPurchaseCost = isRevokingPick ? null : externalPurchaseCost;

      setValue('purchaseCost', restoredPurchaseCost ?? null);
      setValue('costSource.vendorQuoteNumber', null);
      // The save mapper reads the form value; leaving the pick would re-post it.
      setValue('selectedSpa', null, { shouldDirty: true });
      setIsSelectedSpaNotOffered(false);
      // Merge, not wipe, or the external block flashes. The source metadata goes: CostSource
      // renders it without consulting costSourceType.
      setCostData((prev) => ({
        ...prev,
        costQuantityDiscountsApplied: null,
        costSourceDiscountApplyTo: null,
        costSourceDiscountType: null,
        costSourceDiscountValue: null,
        costSourceId: null,
        costSourcePricingProfileId: null,
        costSourcePricingProfileName: null,
        ...EMPTY_VENDOR_QUOTE_COST_DATA,
        costSourceStatus: null,
        costSourceType: null,
        selectedSpa: null,
      }));

      if (isAlternateVersionEnabled) {
        // A sourcing change is a mandatory refetch — the cost values alone may look unchanged.
        forceNextPricesRef.current = true;
        await calculatePrices();
      }
    },
    [calculatePrices, isAlternateVersionEnabled, setCostData, setIsSelectedSpaNotOffered, setValue]
  );

  // The BE clears a typed cost's dates on a vendor change, and a save re-posting them would win.
  const clearTypedCostValidity = useCallback(() => {
    isTypedCostValidityClearedRef.current = true;
    clearCostValidityDates(setValue);
  }, [setValue]);

  const applyCostingCustomChange = useCallback(
    async (checked: boolean) => {
      const wasCostingCustom = !!getValues('isCostingCustom');
      // For programmatic callers; on a Switch click RHF writes the same value right after.
      setValue('isCostingCustom', checked);

      if (wasCostingCustom !== checked) {
        // Each kind of cost starts from the dates the user gave the saved one of its kind: a typed
        // until must never be posted as a vendor quote's, which the BE copies to other lines.
        const savedLine =
          isTypedCostValidityClearedRef.current || !quotePart?.id ? null : quotePart;
        const { validSince, validUntil } = checked
          ? getTypedCostValidityDefaults(savedLine)
          : getVendorQuoteValidityDefaults(savedLine);

        setValue(COST_VALID_SINCE_FIELD_NAME, validSince);
        setValue(COST_VALID_UNTIL_FIELD_NAME, validUntil);
      }

      if (!checked) {
        setValue('prioritizeLegacyCost', false);
        await restoreAutomaticCost();

        return;
      }

      const isReplacingPick = !!(getValues('selectedSpa') as ISelectedSpaFormValue | null)
        ?.spaCostSource;

      setValue('selectedSpa', null);
      setIsSelectedSpaNotOffered(false);
      setCostData((prev) => ({
        ...QuotePartPricingHelper.sanitizeCostDataForSourceType(prev, PricingSourceType.CUSTOM),
        // Cached while the pick was held, so a later Overwrite off posts no cost.
        ...(isReplacingPick ? { defaultPurchaseCost: null, externalPurchaseCost: null } : {}),
      }));
    },
    [getValues, quotePart, restoreAutomaticCost, setCostData, setIsSelectedSpaNotOffered, setValue]
  );

  const onCostingCustomChange = useCallback(
    async ({ target: { checked } }) => {
      const selectedSpa = getValues('selectedSpa') as ISelectedSpaFormValue | null;

      if (checked && selectedSpa) {
        // The Switch writes to RHF after this handler, which would overwrite a synchronous revert.
        setTimeout(() => setValue('isCostingCustom', false));
        setPendingReplaceSelectedSpa({ agreementCode: selectedSpa.spaCostSource });

        return;
      }

      await applyCostingCustomChange(checked);
    },
    [applyCostingCustomChange, getValues, setValue]
  );

  const confirmReplaceSelectedSpa = useCallback(async () => {
    setPendingReplaceSelectedSpa(null);

    await applyCostingCustomChange(true);
  }, [applyCostingCustomChange]);

  const cancelReplaceSelectedSpa = useCallback(() => {
    setPendingReplaceSelectedSpa(null);
  }, []);

  // A failed /prices must not leave the pick half-applied (new cost, old price and margin).
  const withSelectedSpaRollback = useCallback(
    async (change: () => Promise<void>) => {
      const previous = {
        costData: costDataRef.current,
        isCostingCustom: getValues('isCostingCustom'),
        isSelectedSpaNotOffered: isSelectedSpaNotOfferedRef.current,
        purchaseCost: getValues('purchaseCost'),
        selectedSpa: getValues('selectedSpa'),
        validSince: getValues(COST_VALID_SINCE_FIELD_NAME),
        validUntil: getValues(COST_VALID_UNTIL_FIELD_NAME),
        vendorQuoteNumber: getValues('costSource.vendorQuoteNumber'),
      };

      try {
        await change();
      } catch (error) {
        setValue('selectedSpa', previous.selectedSpa, { shouldDirty: true });
        setValue('isCostingCustom', previous.isCostingCustom);
        setValue('purchaseCost', previous.purchaseCost);
        setValue('costSource.vendorQuoteNumber', previous.vendorQuoteNumber);
        setValue(COST_VALID_SINCE_FIELD_NAME, previous.validSince);
        setValue(COST_VALID_UNTIL_FIELD_NAME, previous.validUntil);
        setIsSelectedSpaNotOffered(previous.isSelectedSpaNotOffered);
        setCostData(previous.costData);
        setPurchaseCostExceedsStandard(
          QuotePartPricingHelper.doesPurchaseCostExceedStandard(
            previous.purchaseCost,
            getValues(standardCostFieldName),
            previous.costData?.costSourceType
          )
        );

        throw error;
      }
    },
    [getValues, setCostData, setIsSelectedSpaNotOffered, setValue, standardCostFieldName]
  );

  const selectSpa = useCallback(
    (agreement: ITradeAgreement) =>
      withSelectedSpaRollback(async () => {
        const selectedSpa = QuotePartPricingHelper.mapTradeAgreementToSelectedSpa(agreement);

        setIsSelectedSpaNotOffered(false);
        setValue('selectedSpa', selectedSpa, { shouldDirty: true });
        setValue('isCostingCustom', false);
        setValue('purchaseCost', selectedSpa.spaCost ?? null);
        // The BE stores no vendor quote number for a selected line, and no typed dates.
        setValue('costSource.vendorQuoteNumber', null);
        clearCostValidityDates(setValue);
        setPurchaseCostExceedsStandard(false);

        setCostData((prev) =>
          QuotePartPricingHelper.sanitizeCostDataForSourceType(
            { ...prev, selectedSpa },
            PricingSourceType.SELECTED_SPA
          )
        );

        // A sourcing change is a mandatory refetch.
        forceNextPricesRef.current = true;
        await calculatePrices();
      }),
    [calculatePrices, setCostData, setIsSelectedSpaNotOffered, setValue, withSelectedSpaRollback]
  );

  const clearSelectedSpa = useCallback(
    () =>
      withSelectedSpaRollback(async () => {
        setValue('isCostingCustom', false);

        await restoreAutomaticCost(true);
      }),
    [restoreAutomaticCost, setValue, withSelectedSpaRollback]
  );

  const onPrioritizeLegacyCostChange = useCallback(
    async ({ target: { checked } }: ChangeEvent<HTMLInputElement>) => {
      setValue('prioritizeLegacyCost', checked);
      await calculatePrices();
    },
    [calculatePrices, setValue]
  );

  const onChangeShipmentType = useCallback(async () => {
    await calculatePrices();
  }, [calculatePrices]);

  const onChangeCorporateLocation = useCallback(async () => {
    await calculatePrices();
  }, [calculatePrices]);

  const onChangePricingDate = useCallback(async () => {
    await calculatePrices();
  }, [calculatePrices]);

  const onChangePricingStrategy = useCallback(async () => {
    await calculatePrices();

    setPreviousPricingValues((prev) => ({
      ...prev,
      pricingStrategy: (getValues('pricingStrategy') as PricingStrategy) ?? null,
    }));
  }, [calculatePrices, getValues]);

  const onPriceChange = useCallback(
    (price: number) => {
      changePriceSource(price ? PricingSourceType.CUSTOM : null);
    },
    [changePriceSource]
  );

  return {
    changePriceSource,
    costData,
    // costData stays this very object until the cost is repriced, re-sourced or picked.
    initialCostData,
    setCostData,
    priceData,
    setPriceData,
    priceDecimals,
    setPriceDecimals,
    standardCostFieldName,
    marginFieldName,
    isAlternateVersionEnabled,
    disablePricingFields,
    isExternalDisabled,
    setExternalDisabled,
    unifiedPartStatus,
    setUnifiedPartStatus,
    isCalculating,
    isCalculatingRef,
    setIsCalculating,
    focusFieldWhileCalculating,
    kickPricingRequest,
    purchaseCostExceedsStandard,
    forceNextPrices,
    selectSpa,
    clearSelectedSpa,
    clearTypedCostValidity,
    isReplaceSelectedSpaConfirmationOpen: null !== pendingReplaceSelectedSpa,
    replaceSelectedSpaAgreementCode: pendingReplaceSelectedSpa?.agreementCode,
    confirmReplaceSelectedSpa,
    cancelReplaceSelectedSpa,
    onCostingCustomChange,
    onPrioritizeLegacyCostChange,
    onBlurPurchaseCost,
    onChangePurchaseCost,
    onChangeShipmentType,
    onChangeCorporateLocation,
    onChangePricingDate,
    onChangePricingStrategy,
    onPriceChange,
    processPricingResponse,
    calculatePrices,
    calculateStandardCostForReplacementCost,
    resetReplacementStandardCostDedup,
    resetQuantityBreaks,
    isSpaSelectionAvailable,
    resetSpaSelectionAvailable,
    isSelectedSpaNotOffered,
    setIsSelectedSpaNotOffered,
    buildSelectSpaRequest,
    markSelectSpaPress,
    isSelectSpaPressed,
    pricingWindowMessage,
    initializePreviousPricingValues,
    lastPricesRef,
    notificationPosition,
  };
};

export const [PricingStateProvider, usePricingStateContext] = constate(usePricingState);
